/**
 * arrowGeometry — the one implementation of the guided-tour pointer arrow's maths, shared by
 * TutorialPointerArrow.tsx (every rail/coach/creator card) and CoachMark.tsx (Quick Orient and the
 * Script tab's ScriptOrient). Pure functions over rects, so the whole thing is unit-testable
 * without a layout engine — see __tests__/arrowGeometry.test.ts.
 *
 * It exists because of two owner reports on 20260922:
 *
 * 1. THE ENDPOINT (the bug). CoachMark still clamped the dialog's departure corner INTO the target
 *    rect to find the tip ("nearest edge point"), which for a corner far below-left of a narrow
 *    70px tab lands the tip on `rect.left - 6` — the tab's far corner, i.e. over its NEIGHBOUR.
 *    The owner's screenshot of the Prep/Breakdown sub-tabs is exactly that: the Cast arrow landed
 *    6px left of Cast (over Script), the Locations arrow 6px left of Locations (over Cast).
 *    TutorialPointerArrow had already replaced that rule with `endpointTowardCenter` below; this
 *    module is that rule lifted verbatim so CoachMark cannot diverge from it again.
 *
 * 2. THE ORIGIN (the request). "They attach to the outside of the tutorial panel so it sometimes is
 *    hard to determine where they're starting from. Is there a way we can attach them to something
 *    inside instead? (like the left or right edge of the call to action text)". So `pickDeparture`
 *    now takes an ORIGIN rect — the card's call-to-action text run, or its header title (the
 *    consumers choose, by which side of the card the target is on — see their own rules) — and
 *    departs from whichever of its four edges faces the target, at the point where the line to the
 *    target crosses it (owner, later the same day: "let the arrows start from whichever edge makes
 *    sense"); `buildArrowPath` then leaves that text square to that edge as one continuous curve
 *    (control point 48px past the card's edge on that side) so the arrow crosses the card's
 *    padding and bends in open air. The old corner rule is kept as the fallback for a card that
 *    has no identifiable origin text (or whose text has not been laid out yet), so nothing
 *    regresses where the origin cannot be found.
 *
 * 20261002 — plus `placeDialogClearOfTarget`, the dialog placement CoachMark applies for a host
 * that passes `avoidTarget` (the Shot Editor coach marks): the same kind of pure rect maths, kept
 * here so CoachMark.tsx goes on exporting components only.
 */

/** Anything with the four edges. A DOMRect satisfies it, and so does a plain test fixture. */
export interface EdgeRect {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface Point {
  x: number;
  y: number;
}

/** Which edge of the ORIGIN the arrow leaves by, when it departs from an inside origin — and
 *  therefore which side of the card its exit control point is pushed past. */
export type ExitSide = 'left' | 'right' | 'top' | 'bottom';

export interface ArrowPath {
  line: string;
  head: string;
  len: number;
  w: number;
  h: number;
}

/** Air gap between the arrowhead and the thing it points at. */
export const ENDPOINT_PAD = 6;
/** Air gap between the origin text's glyphs and the arrow's start. */
const ORIGIN_PAD = 2;
/** How far past the card's own edge the exit control point sits — enough that the curve is still
 *  travelling horizontally as it crosses the card's border, rather than already turning. */
const EXIT_REACH = 48;
const HEAD_LEN = 10;
/** Half-angle of the arrowhead's V, in radians. */
const HEAD_SPREAD = 0.4;

export function rectCenter(rect: EdgeRect): Point {
  return { x: (rect.left + rect.right) / 2, y: (rect.top + rect.bottom) / 2 };
}

/**
 * The point where the segment from `from` to the target's CENTER crosses the target rect grown
 * `pad` on every side. Aiming at the centre (rather than clamping `from` onto the nearest edge) is
 * what keeps the tip inside the target's own span no matter how oblique the approach — see reason
 * 1 in the header.
 */
export function endpointTowardCenter(target: EdgeRect, from: Point, pad = ENDPOINT_PAD): Point {
  const c = rectCenter(target);
  const vx = from.x - c.x;
  const vy = from.y - c.y;
  if (vx === 0 && vy === 0) return c;

  const hw = (target.right - target.left) / 2 + pad;
  const hh = (target.bottom - target.top) / 2 + pad;
  // Clamped to 1: a departure point already inside the padded rect aims at itself instead of
  // projecting past it.
  const t = Math.min(
    vx !== 0 ? hw / Math.abs(vx) : Infinity,
    vy !== 0 ? hh / Math.abs(vy) : Infinity,
    1,
  );
  return { x: c.x + vx * t, y: c.y + vy * t };
}

export interface DepartureRequest {
  /** The card/dialog the arrow belongs to. Also the box the exit control point is measured from. */
  anchorRect: EdgeRect;
  /** The element INSIDE the card the arrow should appear to come from (the CTA text run), or null
   *  when there is none to measure — see the fallback below. */
  originRect: EdgeRect | null;
  /** Where the arrow is going; decides which edge of the origin faces it. */
  targetCenter: Point;
  /** Fallback rule when `originRect` is null: score the anchor's four corners, or pin top-right. */
  startCorner: 'auto' | 'top-right';
}

export interface Departure {
  point: Point;
  /** null = the corner fallback, which keeps the old perpendicular-bulge curve. */
  exitSide: ExitSide | null;
}

export function pickDeparture({ anchorRect, originRect, targetCenter, startCorner }: DepartureRequest): Departure {
  if (originRect) {
    // WHICHEVER EDGE FACES THE TARGET (owner 20260922: "Instead of limiting to the left/right edge
    // for the start, let the arrows start from whichever edge makes sense … we can just have it
    // start from the top edge of that label"). The departure is where the line from the origin's
    // centre to the target crosses the origin's own box — the same ray-box rule the tip uses at the
    // other end — so a target above a wide label leaves from its top edge, a target level with it
    // from its side, and the point slides along that edge toward the target rather than sitting on
    // the midpoint. The edge itself is what the exit curve is pushed away from.
    const point = endpointTowardCenter(originRect, targetCenter, ORIGIN_PAD);
    const c = rectCenter(originRect);
    const vx = targetCenter.x - c.x;
    const vy = targetCenter.y - c.y;
    const hw = (originRect.right - originRect.left) / 2 + ORIGIN_PAD;
    const hh = (originRect.bottom - originRect.top) / 2 + ORIGIN_PAD;
    // The limiting axis of that crossing is the edge: a horizontal-ish ray (relative to the box's
    // own proportions) leaves by a side, a vertical-ish one by the top or bottom. A target dead on
    // the centre (vx = vy = 0) has no direction; it reads as "right", the line's reading end.
    const bySide = Math.abs(vx) * hh >= Math.abs(vy) * hw;
    const exitSide: ExitSide = bySide
      ? (vx < 0 ? 'left' : 'right')
      : (vy < 0 ? 'top' : 'bottom');
    return { point, exitSide };
  }

  if (startCorner === 'top-right') {
    return { point: { x: anchorRect.right, y: anchorRect.top }, exitSide: null };
  }

  const corners: Point[] = [
    { x: anchorRect.left, y: anchorRect.top },
    { x: anchorRect.right, y: anchorRect.top },
    { x: anchorRect.left, y: anchorRect.bottom },
    { x: anchorRect.right, y: anchorRect.bottom },
  ];
  let best = corners[0];
  let minDist = Infinity;
  for (const c of corners) {
    const d = Math.hypot(c.x - targetCenter.x, c.y - targetCenter.y);
    if (d < minDist) { minDist = d; best = c; }
  }
  return { point: best, exitSide: null };
}

export interface ArrowPathRequest {
  departure: Point;
  exitSide: ExitSide | null;
  anchorRect: EdgeRect;
  tip: Point;
  /** Defaults to the live window; injected by the tests, which have no layout but do have a size. */
  viewport?: { w: number; h: number };
  /** 20261002 — size the draw-in dash by the CONTROL POLYGON (start → control → tip, never shorter
   *  than the curve) instead of 1.15 × the chord. The chord estimate runs short on a curve that
   *  bends back on itself, and the dash then leaves the curve's last stretch undrawn, short of the
   *  head. Opt-in (CoachMark's avoidTarget hosts), so every other arrow is drawn exactly as before. */
  fullLength?: boolean;
}

/**
 * The quadratic curve, its arrowhead, and the viewBox both consumers draw it into.
 *
 * ONE CONTINUOUS CURVE, always (owner 20260922: "I'm not loving the look of the arrow going
 * straight out to exit the tutorial before curving to the target. Can we ditch that and just have
 * be a continuous curved line?" — a straight run along the CTA row to the card's edge was tried
 * and rejected the same day). With an `exitSide` the control point sits EXIT_REACH outside the
 * card on that side, in line with the departure (level with it for a side exit, plumb with it for
 * a top or bottom one): the curve therefore leaves the text square to the edge it departs by,
 * crosses the card's padding, and bends in open air beyond the card's edge. Keeping
 * it OFF the card's body is the ORIGIN's job, not this curve's — the consumers pick an origin on
 * the target's side of the card (see TutorialPointerArrow's header/CTA rule and CoachMark's
 * title/Next rule), which is the owner's own fallback: "If it covers the text too much we can
 * switch up the starting point from the call to action at the bottom to the header at the top".
 * Without an exit side (the corner fallback) the control point keeps the original perpendicular
 * bulge, so nothing about the old look changes where the old rule still applies.
 */
export function buildArrowPath({ departure, exitSide, anchorRect, tip, viewport, fullLength }: ArrowPathRequest): ArrowPath {
  const { x: dx, y: dy } = departure;
  const { x: tx, y: ty } = tip;
  const len = Math.hypot(tx - dx, ty - dy);

  let cx: number;
  let cy: number;
  if (exitSide === 'left' || exitSide === 'right') {
    cx = exitSide === 'left' ? anchorRect.left - EXIT_REACH : anchorRect.right + EXIT_REACH;
    cy = dy;
  } else if (exitSide === 'top' || exitSide === 'bottom') {
    cx = dx;
    cy = exitSide === 'top' ? anchorRect.top - EXIT_REACH : anchorRect.bottom + EXIT_REACH;
  } else {
    const midX = (dx + tx) / 2;
    const midY = (dy + ty) / 2;
    const curveAmount = Math.min(30, len * 0.15);
    cx = midX + -(ty - dy) / (len || 1) * curveAmount;
    cy = midY + (tx - dx) / (len || 1) * curveAmount;
  }

  // The head follows the curve's final tangent, which for a quadratic is the control point → tip
  // direction. Same in both arms, so an exit-side arrow's head is square to the way it arrived.
  const angle = Math.atan2(ty - cy, tx - cx);
  const a1x = tx - HEAD_LEN * Math.cos(angle - HEAD_SPREAD);
  const a1y = ty - HEAD_LEN * Math.sin(angle - HEAD_SPREAD);
  const a2x = tx - HEAD_LEN * Math.cos(angle + HEAD_SPREAD);
  const a2y = ty - HEAD_LEN * Math.sin(angle + HEAD_SPREAD);

  return {
    line: `M${dx},${dy} Q${cx},${cy} ${tx},${ty}`,
    head: `M${a1x},${a1y} L${tx},${ty} L${a2x},${a2y}`,
    // Approximate quadratic bezier length, for a draw-in speed that does not depend on the curve.
    len: fullLength ? Math.hypot(cx - dx, cy - dy) + Math.hypot(tx - cx, ty - cy) : len * 1.15,
    w: viewport?.w ?? window.innerWidth,
    h: viewport?.h ?? window.innerHeight,
  };
}

/** How far a CoachMark dialog is moved off its target, in CSS pixels (a `translate`). */
export interface DialogShift {
  dx: number;
  dy: number;
}

export const NO_DIALOG_SHIFT: DialogShift = { dx: 0, dy: 0 };

/** Air kept between a moved dialog and the target it clears, and between it and its backdrop's edge. */
export const DIALOG_CLEARANCE = 8;

/** 20261002 (Play smoke M2) - the gap a MOVED dialog prefers to leave between its box and the
 *  target's. At the bare 8 px clearance the arrow across that gap was a 13-55 px stub that barely
 *  read as an arrow; 48 px gives it a visible run. */
export const DIALOG_PREFERRED_GAP = 48;

export interface DialogPlacementRequest {
  /** The dialog's CENTRED box (its rect with no shift applied). */
  dialog: EdgeRect;
  /** The control the current step points at. */
  target: EdgeRect;
  /** The backdrop's box — the region the dialog must stay inside. */
  bounds: EdgeRect;
  margin?: number;
  /** The gap a moved dialog prefers to leave between itself and the target (never below `margin`). */
  preferredGap?: number;
}

/**
 * 20261002 - CoachMark's `avoidTarget` placement (the Shot Editor coach marks, CanvasOrient.tsx).
 *
 * WHY: the two earlier walks point at web controls laid out around a centred dialog, so the dialog
 * never sits on its own target. The Shot Editor's are NATIVE controls the page cannot lay out, and
 * in a short landscape window the tab column beside the open shot list is about a third of the way
 * in from the edge — under a 420 px centred dialog. An arrow from the dialog to a control it covers
 * would land inside the dialog.
 *
 * THE RULE: when the target grown by `margin` overlaps the centred dialog, move the dialog the
 * SHORTEST distance that clears it — vertically if a vertical move keeps it fully inside `bounds`
 * (with `margin` to spare), else horizontally under the same condition; if neither does, it stays
 * centred (an arrow over the dialog beats a dialog pushed off screen). A dialog that does not
 * overlap its target is never moved, so on most layouts this returns NO_DIALOG_SHIFT.
 *
 * THE GAP (Play smoke M2): which way the dialog moves, and whether it moves at all, is decided at
 * the `margin` clearance exactly as above. The chosen move then goes on, AWAY from the target, until
 * the gap between the two boxes is `preferredGap` (48 px) — or, when the backdrop has no room for
 * that, as far as its `margin` allows: the largest gap that fits, never below `margin`.
 */
export function placeDialogClearOfTarget({
  dialog, target, bounds, margin = DIALOG_CLEARANCE, preferredGap = DIALOG_PREFERRED_GAP,
}: DialogPlacementRequest): DialogShift {
  const grown = {
    left: target.left - margin,
    right: target.right + margin,
    top: target.top - margin,
    bottom: target.bottom + margin,
  };
  const overlaps = grown.left < dialog.right && grown.right > dialog.left
    && grown.top < dialog.bottom && grown.bottom > dialog.top;
  if (!overlaps) return NO_DIALOG_SHIFT;

  const fitsVertically = (dy: number) =>
    dialog.top + dy >= bounds.top + margin && dialog.bottom + dy <= bounds.bottom - margin;
  const fitsHorizontally = (dx: number) =>
    dialog.left + dx >= bounds.left + margin && dialog.right + dx <= bounds.right - margin;
  const shortest = (candidates: number[], fits: (d: number) => boolean): number | undefined =>
    candidates.filter(fits).sort((a, b) => Math.abs(a) - Math.abs(b))[0];

  // The widening (THE GAP above). A clearing move is never zero (the overlap test is strict), so its
  // sign is its direction; the room on that side is what the move left before the backdrop's margin,
  // never negative because the move fits.
  const extra = Math.max(0, preferredGap - margin);
  const widened = (d: number, roomBefore: number, roomAfter: number) =>
    (d < 0 ? d - Math.min(extra, Math.max(0, roomBefore)) : d + Math.min(extra, Math.max(0, roomAfter)));

  // Up so the dialog's bottom meets the grown target's top, or down so its top meets the bottom.
  const dy = shortest([grown.top - dialog.bottom, grown.bottom - dialog.top], fitsVertically);
  if (dy !== undefined) {
    return {
      dx: 0,
      dy: widened(dy, dialog.top + dy - (bounds.top + margin), bounds.bottom - margin - (dialog.bottom + dy)),
    };
  }
  const dx = shortest([grown.left - dialog.right, grown.right - dialog.left], fitsHorizontally);
  if (dx !== undefined) {
    return {
      dx: widened(dx, dialog.left + dx - (bounds.left + margin), bounds.right - margin - (dialog.right + dx)),
      dy: 0,
    };
  }
  return NO_DIALOG_SHIFT;
}

/**
 * 20261002 — the side of `anchor` that faces a target lying STRAIGHT above, below or beside it (its
 * centre inside the anchor's span on the other axis), or null for a target off a corner or inside.
 *
 * Why (avoidTarget hosts only): a dialog moved off its control ends up 8-48 px from it (48 when the
 * backdrop has room, placeDialogClearOfTarget), so the control lies straight off one edge. pickDeparture's ray rule judges the edge by the ORIGIN's proportions,
 * and a short, wide title picks a side exit for anything not almost dead above it — whose exit
 * control point, EXIT_REACH past the dialog's far edge, then loops out and back to a control that
 * sits just over the dialog. Departing from the edge that faces the control keeps the curve short.
 */
export function sideFacingTarget(anchor: EdgeRect, targetCenter: Point): ExitSide | null {
  const withinX = targetCenter.x >= anchor.left && targetCenter.x <= anchor.right;
  const withinY = targetCenter.y >= anchor.top && targetCenter.y <= anchor.bottom;
  if (withinX && targetCenter.y < anchor.top) return 'top';
  if (withinX && targetCenter.y > anchor.bottom) return 'bottom';
  if (withinY && targetCenter.x < anchor.left) return 'left';
  if (withinY && targetCenter.x > anchor.right) return 'right';
  return null;
}

/** Depart from `origin`'s edge on `side`, `pad` outside it, at the point of that edge nearest the
 *  target (so a target past the origin's end leaves from that end's corner). */
export function departureOnSide(origin: EdgeRect, side: ExitSide, targetCenter: Point, pad = ORIGIN_PAD): Departure {
  const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
  switch (side) {
    case 'top':
      return { point: { x: clamp(targetCenter.x, origin.left, origin.right), y: origin.top - pad }, exitSide: 'top' };
    case 'bottom':
      return { point: { x: clamp(targetCenter.x, origin.left, origin.right), y: origin.bottom + pad }, exitSide: 'bottom' };
    case 'left':
      return { point: { x: origin.left - pad, y: clamp(targetCenter.y, origin.top, origin.bottom) }, exitSide: 'left' };
    case 'right':
      return { point: { x: origin.right + pad, y: clamp(targetCenter.y, origin.top, origin.bottom) }, exitSide: 'right' };
  }
}

/** `rect` moved by (dx, dy) — a measured rect re-expressed for a dialog shift that is not (or not
 *  yet) the one the DOM wears. */
export function offsetEdgeRect(rect: EdgeRect, dx: number, dy: number): EdgeRect {
  return { left: rect.left + dx, right: rect.right + dx, top: rect.top + dy, bottom: rect.bottom + dy };
}

/**
 * The origin rect for a card, or null when there is nothing measurable to depart from: no
 * selector, no match, or a match that has not been laid out yet (jsdom, or a card mid-mount). A
 * null here is the signal for `pickDeparture` to fall back to the corner rule.
 */
export function originRectWithin(anchor: Element, selector?: string): EdgeRect | null {
  if (!selector) return null;
  const el = anchor.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 || r.height === 0) return null;
  return r;
}
